/**
 * Cliente WebSocket del frontend.
 * Mantiene la conexión entre el navegador y el agente local, gestiona
 * disponibilidad, solicitudes de instalación y el tracking de tickets para
 * recibir confirmaciones de impresión en tiempo real.
 */

class AgentService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.agentStatus = 'unknown';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.reconnectInterval = 5000;
    this.serverURL = this.getServerURL();
    this.pendingPrints = new Map();
    this.connectingPromise = null;
    this.lastTenant = null;
    this.lastPrinterNumber = null;
  }

  /**
   * Obtiene la URL del servidor según el entorno
   * @returns {string} URL del servidor WebSocket
   */
  getServerURL() {
    if (window.location.hostname === 'localhost') {
      return 'ws://localhost:5000';
    } else {
      return 'wss://erp.lutente.com:5000';
    }
  }

  /**
   * Verifica si el agente está disponible
   * @returns {Promise<boolean>} true si el agente está disponible
   */
  async checkAgentAvailability() {
    try {
      const connected = await this.ensureConnection();
      return connected;
    } catch (error) {
      console.log('🔍 Agente no disponible:', error.message);
      return false;
    }
  }

  /**
   * Conecta al agente via WebSocket
   * @returns {Promise<boolean>} true si se conecta exitosamente
   */
  async connectToAgent() {
    return new Promise((resolve, reject) => {
      try {
        if (this.ws) {
          if (this.ws.readyState === WebSocket.OPEN) {
            this.isConnected = true;
            this.agentStatus = 'connected';
            return resolve(true);
          }
          if (this.ws.readyState === WebSocket.CONNECTING) {
            const handleOpen = () => {
              cleanup();
              this.isConnected = true;
              this.agentStatus = 'connected';
              resolve(true);
            };
            const handleError = (err) => {
              cleanup();
              reject(err);
            };
            const cleanup = () => {
              this.ws.removeEventListener('open', handleOpen);
              this.ws.removeEventListener('error', handleError);
            };
            this.ws.addEventListener('open', handleOpen, { once: true });
            this.ws.addEventListener('error', handleError, { once: true });
            return;
          }
          try {
            this.ws.close();
          } catch (closeErr) {
            console.warn('⚠️ Error cerrando WebSocket previo:', closeErr.message);
          }
        }

        // Crear conexión WebSocket
        this.ws = new WebSocket(this.serverURL);

        // Timeout para la conexión
        const timeout = setTimeout(() => {
          if (this.ws) {
            this.ws.close();
          }
          reject(new Error('Timeout de conexión'));
        }, 5000);

        this.ws.onopen = () => {
          clearTimeout(timeout);
          console.log('✅ Conectado al servidor WebSocket');
          this.isConnected = true;
          this.agentStatus = 'connected';
          this.reconnectAttempts = 0;
          
          // Registrar como cliente web
          this.registerAsClient();
          
          if (this.connectingPromise) {
            // resolve chained promise if ensureConnection is waiting
          }
          resolve(true);
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleAgentMessage(message);
          } catch (error) {
            console.error('❌ Error procesando mensaje del agente:', error);
          }
        };

        this.ws.onclose = () => {
          clearTimeout(timeout);
          console.log('🔌 Desconectado del agente');
          this.isConnected = false;
          this.agentStatus = 'disconnected';
          this.failAllPending('Agente desconectado');
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          clearTimeout(timeout);
          console.error('❌ Error de conexión al agente:', error);
          this.isConnected = false;
          this.agentStatus = 'error';
          this.failAllPending('Error de conexión con agente');
          reject(error);
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  async ensureConnection() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isConnected) {
      return true;
    }

    if (this.connectingPromise) {
      return this.connectingPromise;
    }

    this.connectingPromise = this.connectToAgent()
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        this.connectingPromise = null;
      });

    return this.connectingPromise;
  }

  /**
   * Maneja mensajes del agente
   * @param {Object} message - Mensaje del agente
   */
  handleAgentMessage(message) {
    console.log('📨 Mensaje del agente:', message);

    switch (message.type) {
      case 'connection_established':
        this.handleConnectionEstablished(message.data);
        break;
      case 'registration_success':
        this.handleRegistrationSuccess(message.data);
        break;
      case 'print_sent':
        this.handlePrintSent(message.data);
        break;
      case 'agent_status':
        this.handleAgentStatus(message.data);
        break;
      case 'print_success':
        this.handlePrintSuccess(message.data);
        break;
      case 'print_error':
        this.handlePrintError(message.data);
        break;
      case 'pong':
        // Respuesta al ping
        break;
      default:
        console.warn('⚠️ Tipo de mensaje desconocido:', message.type);
    }
  }

  /**
   * Registra el cliente web en el servidor
   */
  registerAsClient() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const registrationMessage = {
        type: 'register_client',
        data: {
          userAgent: navigator.userAgent,
          tenant: this.getTenantFromCookies() || this.lastTenant || '',
          usuario: this.getUsuarioFromCookies(),
          printerNumber: this.getPrinterNumberFromCookies() || this.lastPrinterNumber
        }
      };
      
      this.ws.send(JSON.stringify(registrationMessage));
      console.log('📝 Registrando cliente web en el servidor...');
    }
  }

  /**
   * Obtiene el tenant de las cookies
   * @returns {string} Nombre del tenant
   */
  getTenantFromCookies() {
    return this.getCookieValue('tenant') ||
      (() => { try { return sessionStorage.getItem('tenant'); } catch (_) { return null; } })() ||
      this.lastTenant || 'unknown';
  }

  /**
   * Obtiene el usuario de las cookies
   * @returns {string} Nombre del usuario
   */
  getUsuarioFromCookies() {
    return this.getCookieValue('usuario') ||
      (() => { try { return sessionStorage.getItem('usuario'); } catch (_) { return null; } })() ||
      '';
  }

  getPrinterNumberFromCookies() {
    return this.getCookieValue('printerNumber') || this.lastPrinterNumber || null;
  }

  getCookieValue(name) {
    const pattern = `(?:^|; )${name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1')}=([^;]*)`;
    const match = document.cookie.match(new RegExp(pattern));
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Maneja la conexión establecida
   * @param {Object} data - Datos de la conexión
   */
  handleConnectionEstablished(data) {
    console.log('✅ Conexión WebSocket establecida:', data.message);
    this.isConnected = true;
    this.agentStatus = 'connected';
    this.reconnectAttempts = 0;
  }

  /**
   * Maneja el éxito del registro
   * @param {Object} data - Datos del registro
   */
  handleRegistrationSuccess(data) {
    console.log('✅ Cliente registrado correctamente:', data.message);
    console.log('📊 Agentes disponibles:', data.availableAgents || 0);
  }

  handlePrintSent(data) {
    const { requestId, agentId } = data || {};
    if (!requestId) {
      return;
    }
    const pending = this.pendingPrints.get(requestId);
    if (pending) {
      pending.status = 'sent';
      console.log(`📤 Ticket ${pending.saleId || '-'} asignado al agente ${agentId || 'desconocido'}`);
    }
  }

  /**
   * Maneja el estado del agente
   * @param {Object} data - Datos del estado
   */
  handleAgentStatus(data) {
    this.agentStatus = data.status;
    console.log(`📊 Estado del agente: ${data.status}`);
  }

  /**
   * Maneja éxito de impresión
   * @param {Object} data - Datos del éxito
   */
  handlePrintSuccess(data) {
    const { requestId } = data || {};
    console.log('✅ Impresión exitosa:', data?.message || 'Ticket impreso');
    if (!requestId) {
      return;
    }
    const pending = this.pendingPrints.get(requestId);
    if (!pending) {
      console.warn(`⚠️ No se encontró solicitud pendiente para requestId=${requestId}`);
      return;
    }
    clearTimeout(pending.timeoutId);
    pending.resolve({ success: true, data });
    this.pendingPrints.delete(requestId);
  }

  /**
   * Maneja error de impresión
   * @param {Object} data - Datos del error
   */
  handlePrintError(data) {
    const { requestId } = data || {};
    console.error('❌ Error de impresión:', data?.error || 'Error desconocido');
    if (requestId) {
      const pending = this.pendingPrints.get(requestId);
      if (pending) {
        clearTimeout(pending.timeoutId);
        const error = new Error(data?.error || 'Error de impresión');
        error.payload = data;
        pending.reject(error);
        this.pendingPrints.delete(requestId);
        return;
      }
    }
    console.warn('⚠️ Error de impresión sin requestId asociado');
  }

  /**
   * Intenta reconectar al agente
   */
  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`🔄 Intentando reconectar... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connectingPromise = this.connectToAgent()
          .catch(() => {
          // Error silencioso, ya se maneja en attemptReconnect
          })
          .finally(() => {
            this.connectingPromise = null;
          });
      }, this.reconnectInterval);
    } else {
      console.error('❌ Máximo de intentos de reconexión alcanzado');
      this.agentStatus = 'failed';
      this.failAllPending('No se pudo reconectar con el agente');
    }
  }

  /**
   * Envía un mensaje al agente
   * @param {string} type - Tipo de mensaje
   * @param {Object} data - Datos del mensaje
   */
  sendMessage(type, data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = {
        type,
        data,
        timestamp: new Date().toISOString()
      };
      
      this.ws.send(JSON.stringify(message));
      console.log('📤 Mensaje enviado al agente:', type);
    } else {
      console.warn('⚠️ No conectado al agente, no se puede enviar mensaje');
    }
  }

  /**
   * Envía un ticket para imprimir
   * @param {Object} ticketData - Datos del ticket
   * @returns {Promise<boolean>} true si se envía exitosamente
   */
  async printTicket(ticketData) {
    await this.ensureConnection();

    const requestId = this.generateRequestId();
    const tenant = String(ticketData.tenant || this.getTenantFromCookies() || '').trim();
    const printerNumber = String(ticketData.printerNumber || this.getPrinterNumberFromCookies() || '').trim();

    this.lastTenant = tenant || this.lastTenant;
    this.lastPrinterNumber = printerNumber || this.lastPrinterNumber;

    const payload = {
      ...ticketData,
      tenant,
      printerNumber,
      requestId
    };

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.registerAsClient();
    }

    console.log('🖨️ Enviando ticket para imprimir:', ticketData.sale?.id, 'requestId=', requestId);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        if (this.pendingPrints.has(requestId)) {
          this.pendingPrints.delete(requestId);
        }
        reject(new Error('Timeout de impresión: no hubo respuesta del agente'));
      }, 20000);

      this.pendingPrints.set(requestId, {
        resolve,
        reject,
        timeoutId,
        saleId: ticketData.sale?.id,
        status: 'pending'
      });

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        clearTimeout(timeoutId);
        this.pendingPrints.delete(requestId);
        reject(new Error('Agente no disponible para imprimir'));
        return;
      }

      this.ws.send(JSON.stringify({
        type: 'print_ticket',
        data: payload,
        timestamp: new Date().toISOString()
      }));
      console.log('📤 Mensaje enviado al agente: print_ticket');
    });
  }

  /**
   * Envía un ping al agente
   */
  ping() {
    this.sendMessage('ping', { timestamp: new Date().toISOString() });
  }

  /**
   * Desconecta del agente
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.agentStatus = 'disconnected';
    this.failAllPending('Agente desconectado por el usuario');
  }

  /**
   * Obtiene el estado actual del agente
   * @returns {Object} Estado del agente
   */
  getStatus() {
    return {
      connected: this.isConnected,
      status: this.agentStatus,
      serverURL: this.serverURL
    };
  }

  /**
   * Instala el agente automáticamente
   * @returns {Promise<boolean>} true si se instala exitosamente
   */
  async installAgent() {
    try {
      console.log('🔧 Iniciando instalación automática del agente...');
      
      // Mostrar diálogo de confirmación
      const confirmed = await this.showInstallDialog();
      if (!confirmed) {
        return false;
      }

      // Descargar e instalar el agente
      const success = await this.downloadAndInstallAgent();
      
      if (success) {
        console.log('✅ Agente instalado correctamente');
        // Intentar conectar después de la instalación
        setTimeout(() => {
          this.connectToAgent().catch(() => {
            console.log('⚠️ No se pudo conectar después de la instalación');
          });
        }, 3000);
        return true;
      } else {
        throw new Error('Error en la instalación');
      }
      
    } catch (error) {
      console.error('❌ Error instalando agente:', error);
      throw error;
    }
  }

  /**
   * Muestra diálogo de instalación
   * @returns {Promise<boolean>} true si el usuario confirma
   */
  async showInstallDialog() {
    return new Promise((resolve) => {
      const confirmed = window.confirm(
        'Para imprimir tickets automáticamente, necesita instalar el agente local.\n\n' +
        '¿Desea descargar e instalar el agente ahora?\n\n' +
        'Esto solo tomará unos minutos y le permitirá imprimir tickets sin intervención manual.'
      );
      resolve(confirmed);
    });
  }

  /**
   * Descarga e instala el agente
   * @returns {Promise<boolean>} true si se instala exitosamente
   */
  async downloadAndInstallAgent() {
    try {
      // Obtener URL de descarga según la plataforma
      const downloadURL = this.getAgentDownloadURL();
      
      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = this.getAgentFileName();
      
      // Simular clic para iniciar descarga
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Mostrar instrucciones de instalación
      this.showInstallationInstructions();
      
      return true;
      
    } catch (error) {
      console.error('❌ Error descargando agente:', error);
      throw error;
    }
  }

  /**
   * Obtiene la URL de descarga del agente
   * @returns {string} URL de descarga
   */
  getAgentDownloadURL() {
    const baseURL = window.location.hostname === 'localhost' 
      ? 'http://localhost:5000' 
      : 'https://erp.lutente.com:5000';
    
    const platform = this.detectPlatform();
    
    if (platform === 'windows') {
      return `${baseURL}/agent/ERP-Ticket-Agent-win.zip`;
    } else if (platform === 'macos') {
      return `${baseURL}/agent/ERP-Ticket-Agent-mac.zip`;
    } else {
      return `${baseURL}/agent/ERP-Ticket-Agent.AppImage`;
    }
  }

  /**
   * Obtiene el nombre del archivo del agente
   * @returns {string} Nombre del archivo
   */
  getAgentFileName() {
    const platform = this.detectPlatform();

    if (platform === 'windows') {
      return 'ERP-Ticket-Agent-win.zip';
    } else if (platform === 'macos') {
      return 'ERP-Ticket-Agent-mac.zip';
    } else {
      return 'ERP-Ticket-Agent.AppImage';
    }
  }

  /**
   * Detecta la plataforma del usuario
   * @returns {string} Plataforma detectada
   */
  detectPlatform() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('win')) {
      return 'windows';
    } else if (userAgent.includes('mac')) {
      return 'macos';
    } else {
      return 'linux';
    }
  }

  /**
   * Muestra instrucciones de instalación
   */
  showInstallationInstructions() {
    const platform = this.detectPlatform();
    let instructions = '';
    
    if (platform === 'windows') {
      instructions = 
        'Instrucciones de instalación:\n\n' +
        '1. Descomprima el archivo ERP-Ticket-Agent-win.zip\n' +
        '2. Ejecute ERP-Ticket-Agent-Setup.exe\n' +
        '3. Siga las instrucciones del instalador\n' +
        '4. El agente se iniciará automáticamente\n' +
        '5. Vuelva a intentar imprimir el ticket';
    } else if (platform === 'macos') {
      instructions = 
        'Instrucciones de instalación:\n\n' +
        '1. Abra el archivo descargado (ERP-Ticket-Agent-mac.zip)\n' +
        '2. Arrastre la aplicación "ERP Ticket Agent.app" a la carpeta Aplicaciones\n' +
        '3. Abra la aplicación desde Launchpad o Finder\n' +
        '4. Si macOS muestra un aviso de seguridad, vaya a Preferencias > Seguridad y privacidad y permita la ejecución\n' +
        '5. Vuelva a intentar imprimir el ticket';
    } else {
      instructions = 
        'Instrucciones de instalación:\n\n' +
        '1. Haga el archivo ejecutable: chmod +x ERP-Ticket-Agent.AppImage\n' +
        '2. Ejecute el archivo: ./ERP-Ticket-Agent.AppImage\n' +
        '3. Vuelva a intentar imprimir el ticket';
    }
    
    alert(instructions);
  }

  generateRequestId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  failAllPending(reason) {
    if (!this.pendingPrints.size) {
      return;
    }
    this.pendingPrints.forEach(({ reject, timeoutId }) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (typeof reject === 'function') {
        reject(new Error(reason));
      }
    });
    this.pendingPrints.clear();
  }
}

// Instancia singleton del servicio
const agentService = new AgentService();

export default agentService;
